import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../store/auth.store.js';
import { BackgroundEffects } from '../components/lobby/BackgroundEffects.js';
import { SearchBar } from '../components/match-history/SearchBar.js';
import { FilterTabs } from '../components/match-history/FilterTabs.js';
import { HistoryCard } from '../components/match-history/HistoryCard.js';
import type { MatchHistoryItem } from '../components/match-history/HistoryCard.js';
import { EmptyState } from '../components/match-history/EmptyState.js';
import { apiClient } from '../api/client.js';

const MOCK_HISTORY: MatchHistoryItem[] = [
  { id: '1', roomName: 'Sunday Housie Cup', roomCode: 'TAMB99', date: 'June 27, 2026', duration: '05:42', playersCount: 24, rank: 1, prizeWon: 'Full House', status: 'Finished', isWin: true },
  { id: '2', roomName: 'Elite Blitz Speed-Run', roomCode: 'BLITZ01', date: 'June 26, 2026', duration: '03:15', playersCount: 10, rank: 4, prizeWon: null, status: 'Finished', isWin: false },
  { id: '3', roomName: 'Beginner Housie Table', roomCode: 'SLOW22', date: 'June 25, 2026', duration: '08:24', playersCount: 12, rank: 2, prizeWon: 'Early Five', status: 'Finished', isWin: true },
  { id: '4', roomName: 'Weekly Mega Housie', roomCode: 'MEGA40', date: 'June 20, 2026', duration: '12:15', playersCount: 50, rank: 12, prizeWon: null, status: 'Finished', isWin: false },
  { id: '5', roomName: 'Late Night Tambola', roomCode: 'NIGHT8', date: 'June 18, 2026', duration: '06:30', playersCount: 8, rank: 1, prizeWon: 'Full House', status: 'Finished', isWin: true },
];

export function MatchHistoryScreen() {
  const { user, isSessionChecked } = useAuthStore();
  const navigate = useNavigate();

  const [matches, setMatches] = useState<MatchHistoryItem[]>([]);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'today' | 'week' | 'month' | 'wins' | 'losses'>('all');
  const [loadingMore, setLoadingMore] = useState(false);
  const [visibleCount, setVisibleCount] = useState(4);

  // Route protection
  useEffect(() => {
    if (isSessionChecked && !user) {
      navigate('/login');
    }
  }, [user, isSessionChecked, navigate]);

  // Fetch history from server
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await apiClient.get('/users/history');
        if (res.data.success && res.data.data.length > 0) {
          setMatches(res.data.data);
        }
      } catch (err) {
        console.error('Failed to load match history from server:', err);
      }
    };
    fetchHistory();
  }, []);

  const handleBack = () => {
    navigate('/home');
  };

  const handleClear = () => {
    setSearch('');
    setActiveTab('all');
  };

  // Filter & Search validation
  const finalHistory = matches.length > 0 ? matches : MOCK_HISTORY;
  const filteredMatches = finalHistory.filter((match) => {
    // Search match by room code
    const matchesSearch = match.roomCode.toLowerCase().includes(search.toLowerCase());
    
    // Tab filters
    if (activeTab === 'wins') return matchesSearch && match.isWin;
    if (activeTab === 'losses') return matchesSearch && !match.isWin;
    
    // Date intervals
    if (activeTab === 'today') {
      return matchesSearch && match.date.includes('June 27'); // Mock today matching
    }
    if (activeTab === 'week') {
      return matchesSearch && (match.date.includes('June 27') || match.date.includes('June 26') || match.date.includes('June 25'));
    }
    
    return matchesSearch;
  });

  const displayedMatches = filteredMatches.slice(0, visibleCount);

  // Simulated infinite scroll logic
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 5;
    
    if (isAtBottom && !loadingMore && visibleCount < filteredMatches.length) {
      setLoadingMore(true);
      setTimeout(() => {
        setVisibleCount((prev) => prev + 2);
        setLoadingMore(false);
      }, 1000); // 1s simulation delay
    }
  };

  return (
    <div className="relative flex h-screen w-screen flex-col justify-between overflow-hidden">
      {/* 3D background effects */}
      <BackgroundEffects />

      {/* Header bar */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full flex items-center justify-between px-6 py-4 backdrop-blur-md border-b border-white/5 bg-slate-950/20 z-30"
      >
        <button
          onClick={handleBack}
          className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold uppercase tracking-wider text-surface-200 hover:text-white transition-colors cursor-pointer"
        >
          <span>&larr;</span>
          <span>Back</span>
        </button>

        <div className="flex flex-col items-center select-none">
          <h2 className="font-display text-base font-black tracking-wider text-white">
            MATCH HISTORY
          </h2>
          <span className="text-[9px] font-bold tracking-widest text-gradient uppercase">
            Your Multiplayer Game Records
          </span>
        </div>

        {/* Empty status element placeholder */}
        <div className="w-[70px]" />
      </motion.header>

      {/* Subheader Filters bar */}
      <div className="w-full px-6 py-3 bg-slate-950/10 backdrop-blur-sm border-b border-white/5 z-20 flex flex-col sm:flex-row items-center justify-between gap-3">
        <FilterTabs activeTab={activeTab} onChange={setActiveTab} />
        <SearchBar value={search} onChange={setSearch} />
      </div>

      {/* Scrollable Matches List grid wrapper */}
      <main className="flex-1 flex flex-col justify-center items-center px-6 py-4 max-h-[calc(100vh-140px)] z-20 overflow-hidden">
        <div
          onScroll={handleScroll}
          className="w-full max-w-3xl h-full overflow-y-auto space-y-3 pr-2 scrollbar-thin scroll-smooth"
        >
          <AnimatePresence>
            {displayedMatches.length > 0 ? (
              <>
                {displayedMatches.map((match) => (
                  <HistoryCard key={match.id} match={match} />
                ))}

                {/* Infinite scroll loader indicator */}
                {loadingMore && (
                  <div className="flex items-center justify-center py-4 select-none">
                    <div className="w-5 h-5 rounded-full border-2 border-purple-500/20 border-t-purple-500 animate-spin mr-2" />
                    <span className="text-[10px] font-bold text-surface-500 uppercase tracking-widest font-mono">
                      Loading more...
                    </span>
                  </div>
                )}
              </>
            ) : (
              <EmptyState onClearFilters={handleClear} />
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
export default MatchHistoryScreen;